import React from 'react';
import { DateTime } from 'luxon';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ReplayIcon from '@mui/icons-material/Replay';
import AccordionDetails from '@mui/material/AccordionDetails';
import { HistoryItemType, MealType } from '../../../common/commonTypes';
import MealCard from '../../MealCard';
import MacroBullet from '../../MacroBullet';
import MacroBar from '../../MacroBar';
import { useTranslation } from 'react-i18next';

type HistoryDaysType = {
    groupedMeals: HistoryItemType;
    onEditMeal: (meal: MealType) => void;
    onDeleteMeal: (meal: MealType) => void;
    onRepeatMeal: (meal: MealType) => void;
    onFavMeal: (meal: MealType) => void;
};

const HistoryDays = (props: HistoryDaysType) => {
    const { groupedMeals, onEditMeal, onDeleteMeal, onFavMeal, onRepeatMeal } = props;

    const { t, i18n } = useTranslation();

    return (
        <>
            {Object.keys(groupedMeals).map((date, idx) => (
                <Accordion key={date} defaultExpanded={idx === 0}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <div className="w-full">
                            <h3 className="font-bold mb-2">
                                <Typography color="primary">{date}</Typography>
                            </h3>

                            <div className="text-sm mb-2">
                                <b>{t('calories')}:</b> {groupedMeals[date].totals.calories}{' '}
                                {t('cal')}
                            </div>

                            <table className="text-sm">
                                <tbody>
                                    <tr>
                                        <td>
                                            <div className="flex items-center pr-4">
                                                <MacroBullet type="protein" />
                                                <b>{t('protein')}:&nbsp;</b>
                                                {groupedMeals[date].totals.protein} {t('gr')}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex items-center pr-4">
                                                <MacroBullet type="fat" />
                                                <b>{t('fat')}:&nbsp;</b>{' '}
                                                {groupedMeals[date].totals.fats} {t('gr')}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div className="flex items-center">
                                                <MacroBullet type="carb" />
                                                <b> {t('carbs')}:&nbsp;</b>
                                                {groupedMeals[date].totals.carbsNoSugars} {t('gr')}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex items-center">
                                                <MacroBullet type="sugar" />
                                                <b> {t('sugar')}:&nbsp;</b>{' '}
                                                {groupedMeals[date].totals.sugars} {t('gr')}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <MacroBar
                                protein={groupedMeals[date].totals.protein}
                                fat={groupedMeals[date].totals.fats}
                                carb={groupedMeals[date].totals.carbsNoSugars}
                                sugar={groupedMeals[date].totals.sugars}
                            />
                        </div>
                    </AccordionSummary>
                    <AccordionDetails>
                        <ul>
                            {groupedMeals[date].meals.map((meal: MealType, idx: number) => (
                                <li key={idx} className="ml-8">
                                    <MealCard
                                        meal={meal}
                                        onEdit={onEditMeal}
                                        onDelete={onDeleteMeal}
                                        onFav={onFavMeal}
                                        onLog={onRepeatMeal}
                                    />
                                </li>
                            ))}
                        </ul>
                    </AccordionDetails>
                </Accordion>
            ))}
        </>
    );
};

export default HistoryDays;
